import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Typography,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
  createFilterOptions
} from '@mui/material';
import { getApiCall } from 'services/apiHelper';
import { baseUrl } from 'config/config';
import { postApiCall } from 'services/apiHelper';
import { useNavigate, useParams } from 'react-router-dom';


const UserRoleMapForm = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams();
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('Active');
  const [user, setUser] = useState<any>({});
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [userRoleData, setUserRoleData] = useState<any>(null);
  const [errors, setErrors] = useState({ role: false, user: false });
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showSubmitErrorSnackbar, setShowSubmitErrorSnackbar] = useState(false);

  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.value);
  };

  const handleUserChange = (event: SelectChangeEvent) => {
    setUser(event.target.value);
  };

  const handleSubmit = async () => {

    const newErrors = {
      role: !role,
      user: !user && !mode // Only validate user in new mode
    };

    setErrors(newErrors);

    if (newErrors.role || newErrors.user) {
      setShowErrorSnackbar(true);
      return;
    }

    const payload = {
      role: role,
      status: status,
      emailId: user?.emailId || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      itemID: user?.itemId || 0
    };

    try {

      if (mode?.toLowerCase() == "edit") {
        const response = await postApiCall(`${baseUrl}UserRoleMap/Update`, payload);
      } else {
        const response = await postApiCall(`${baseUrl}UserRoleMap/Add`, payload);
      }


      setShowSuccessSnackbar(true);
      setTimeout(() => navigate('/config/user/list'), 2000);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setShowSubmitErrorSnackbar(true);
    }
  };

  const handleCancel = () => {
    navigate('/config/user/list');
  };

  const handleSyncUsers = async () => {
    try {
      await postApiCall(`${baseUrl}UserRoleMap/DeltaUsersFromAzure`, {});
      // Refresh users list after sync
      const response = await getApiCall<any>(`${baseUrl}UsersLeaseApplication/GetAll`);
      const usersWithFullName = response.result.map(user => ({
        ...user,
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim()
      }));
      setUsers(usersWithFullName);
    } catch (error: any) {
      console.error('Error syncing users:', error);
    }
  };

  useEffect(() => {
    const getUserData = async () => {
      try {
        console.time('Users API Call');
        const response = await getApiCall<any>(`${baseUrl}UsersLeaseApplication/GetAll`);
        console.timeEnd('Users API Call');
        console.log('Users data size:', response.result?.length || 0);
        console.log('Sample user:', response.result?.[0]);

        const usersWithFullName = response.result.map(user => ({
          ...user,
          fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.emailId || 'Unknown User'
        }));

        setUsers(usersWithFullName);
      } catch (error: any) {
        console.error('Error in GET request:', error);
        setUsers([]);
      }
    };
    getUserData();
  }, []);


  useEffect(() => {
    const getUserRoleData = async () => {
      try {
        console.time('Roles API Call');
        const response = await getApiCall<any>(`${baseUrl}Roles/GetAll`);
        console.timeEnd('Roles API Call');
        console.log('Roles data size:', response.result?.length || 0);
        setRoles(response.result);
      } catch (error: any) {
        console.error('Error in GET request:', error);
        setRoles([]);
      }
    };
    getUserRoleData();
  }, []);

  useEffect(() => {
    if (id && (mode === 'view' || mode === 'edit')) {
      const fetchUserRoleById = async () => {
        try {
          console.time('UserRole by ID API Call');
          const response = await getApiCall<any>(`${baseUrl}UserRoleMap/GetUserRoleByItemId/${id}`);
          console.timeEnd('UserRole by ID API Call');
          const userData = response.result;
          setUserRoleData(userData);
          setRole(userData.role || '');
          setStatus(userData.status || 'Active');
          console.log(userData);
          
          setUser(userData);
        } catch (error: any) {
          console.error('Error fetching user role by ID:', error);
        }
      };
      fetchUserRoleById();
    }
  }, [id, mode]);



  return (
    <Box sx={{ p: 3, maxWidth: 500 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>Role</Typography>
        <Autocomplete
          sx={{ width: 280 }}
          options={roles}
          getOptionLabel={(option) => option.role}
          value={roles.find(r => r.role === role) || null}
          onChange={(event, newValue) => {
            setRole(newValue?.role || '');
            setErrors(prev => ({ ...prev, role: false }));
          }}
          disabled={mode === 'view'}
          filterOptions={(options, { inputValue }) =>
            options.filter(option =>
              option.role.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={errors.role}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'red',
                    borderWidth: '2px'
                  }
                }
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>Status</Typography>
        <RadioGroup
          row
          value={status}
          onChange={handleStatusChange}
        >
          <FormControlLabel
            value="Active"
            control={<Radio size="small" disabled={mode === 'view'} />}
            label={<Typography sx={{ fontSize: '14px' }}>Active</Typography>}
          />
          <FormControlLabel
            value="Inactive"
            control={<Radio size="small" disabled={mode === 'view'} />}
            label={<Typography sx={{ fontSize: '14px' }}>Inactive</Typography>}
          />
        </RadioGroup>
      </Box>

      {!mode && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>User</Typography>
          <Autocomplete
            sx={{ width: 200, mr: 2 }}
            options={users}
            getOptionKey={(option) => option.itemId}
            getOptionLabel={(option) => option.fullName}
            value={users.find(u => u.itemId === user) || null}
            onChange={(event, newValue) => {
              setUser(newValue?.itemId || '');
              setErrors(prev => ({ ...prev, user: false }));
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter(option => 
                option.fullName.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={errors.user}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '32px',
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'red',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            )}
          />
          <Button
            variant="contained"
            color="success"
            size="small"
            sx={{ height: '32px', whiteSpace: 'nowrap' }}
            onClick={handleSyncUsers}
          >
            Sync New Users
          </Button>
        </Box>
      )}

      {(user || userRoleData) && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>Email Id</Typography>
            <TextField
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  height: '32px'
                }
              }}
              size="small"
              value={userRoleData?.emailId || users.find(u => u.itemId === user)?.emailId || ''}
              disabled
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>First Name</Typography>
            <TextField
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  height: '32px'
                }
              }}
              size="small"
              value={userRoleData?.firstName || users.find(u => u.itemId === user)?.firstName || ''}
              disabled
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 80, mr: 2, fontSize: '14px' }}>Last Name</Typography>
            <TextField
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  height: '32px'
                }
              }}
              size="small"
              value={userRoleData?.lastName || users.find(u => u.itemId === user)?.lastName || ''}
              disabled
              variant="outlined"
            />
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-start' }}>
        {mode === 'view' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ minWidth: '64px' }}
            onClick={() => navigate(`/config/user/edit/${id}`)}
          >
            Edit
          </Button>
        )}
        {mode === 'edit' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ minWidth: '80px' }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        )}
        {!mode && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ minWidth: '80px' }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          sx={{ minWidth: '80px' }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          sx={{ backgroundColor: 'red', color: 'white' }}
        >
          Form could not be submitted<br />
          Please correct the validation errors
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ backgroundColor: 'green', color: 'white' }}
        >
          Form submitted successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSubmitErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSubmitErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSubmitErrorSnackbar(false)}
          severity="error"
          sx={{ backgroundColor: 'red', color: 'white' }}
        >
          Form could not be submitted<br />
          There was an error in adding the user role
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRoleMapForm;
